import { useLazyQuery } from "@apollo/client";
import { useOnErrorGraphql } from "@neonumy/ant-design";
import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { routers } from "config/routes";
import useStore from "config/store";
import { GET_COMPANY_WITH_ACCOUNTING_FIRM } from "queries/centralapi/companies";
import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  CompanyWithAccountingFirmInput,
  CompanyWithAccountingFirmOutput,
} from "types/queries/centralapi/accountingFirm";
import { validateId } from "utils/validateId";

export function useQueryCompanyAccountingFirm() {
  const { onErrorGraphql } = useOnErrorGraphql();
  const location = useLocation();

  const setCompanySelected = useStore(state => state.setCompanySelected);
  const setAccountingFirmSelected = useStore(state => state.setAccountingFirmSelected);
  const setIsCompanySelectOpen = useStore(state => state.setIsCompanySelectOpen);
  const { companyId } = useParams();
  const companySelected = useStore(state => state.companySelected);
  const userData = useStore(state => state.userData);
  const userIsClient = ["client", "client_without_accounting"].includes(
    userData?.userCategory.slug ?? "",
  );

  const [companyWithAccountingFirm, { data, error }] = useLazyQuery<
    CompanyWithAccountingFirmOutput,
    CompanyWithAccountingFirmInput
  >(GET_COMPANY_WITH_ACCOUNTING_FIRM, {
    client: clientCentralApiStrawberry,
  });

  useEffect(() => {
    if (data?.getCompany) {
      const { accountingFirm, ...company } = data.getCompany;
      setCompanySelected({
        id: company.id,
        name: company.name,
        internalReference: company.internalReference,
      });
      if (accountingFirm) {
        setAccountingFirmSelected({ id: accountingFirm.id, name: accountingFirm.name });
      }
    }
  }, [data]);

  useEffect(() => {
    if (error) onErrorGraphql(error);
  }, [error]);

  useEffect(() => {
    if (userIsClient) {
      let companyToFetch = userData.companies?.[0];
      if (companyId && !companySelected) {
        companyToFetch = companyId;
      }

      if (validateId(companyToFetch)) {
        companyWithAccountingFirm({
          variables: {
            id: companyToFetch,
          },
        });

        if (location.pathname === `/` || companyId !== companyToFetch) {
          routers.navigate(`/company/${companyToFetch}`, { replace: true });
        }

        setIsCompanySelectOpen(false);
      }

      if (userData?.companies.length > 1 && !companySelected) {
        setIsCompanySelectOpen(true);
      }
    }
  }, []);

  return {
    companyWithAccountingFirm,
    data,
  };
}
